import { AppConfig } from "../utils/AppConfig.js";

/**
 * Sesión contra LCApii: JWT (15 min) + refresh token rotativo (10 días) en localStorage.
 * Sin sesión, redirige al login central; éste vuelve a login.html con los tokens en el hash.
 */
const LCAPII_URL = AppConfig.lcapii.url;
const LCAPII_SID = AppConfig.lcapii.sid;

const ACCESS_KEY = "lcapii_access";
const REFRESH_KEY = "lcapii_refresh";
const EXP_KEY = "lcapii_access_exp";

// La carcaza login.html vive en el base path de la app (ej. /rrhh2001/login.html)
const LCAPII_LOGIN_SHELL = location.origin + import.meta.env.BASE_URL + "login.html";
const LCAPII_LOGIN_CENTRAL = LCAPII_URL + "/auth/login.html";

export const redirectLogin = () => {
  window.location.href = LCAPII_LOGIN_CENTRAL + "?redirect=" + encodeURIComponent(LCAPII_LOGIN_SHELL);
};

let _refreshInFlight = null;

const doRefresh = async () => {
  if (_refreshInFlight) return _refreshInFlight;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;

  _refreshInFlight = (async () => {
    try {
      const r = await fetch(LCAPII_URL + "/usuario/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ RefreshToken: refreshToken }),
      });
      if (!r.ok) return false;
      const d = await r.json();
      localStorage.setItem(ACCESS_KEY, d.accessToken);
      localStorage.setItem(REFRESH_KEY, d.refreshToken);
      localStorage.setItem(EXP_KEY, String(Date.now() + d.expiresIn * 1000));
      return true;
    } catch {
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
};

/** Token vigente (renueva si está por vencer). Nunca leer localStorage directo desde afuera. */
export const getAccessToken = async () => {
  if (AppConfig.lcapii.skipLogin) return null;
  const exp = parseInt(localStorage.getItem(EXP_KEY) || "0", 10);
  if (!localStorage.getItem(ACCESS_KEY)) return null;
  if (Date.now() >= exp - 30_000) {
    const ok = await doRefresh();
    if (!ok) return null;
  }
  return localStorage.getItem(ACCESS_KEY);
};

export const apiFetch = async (url, opts = {}) => {
  if (AppConfig.lcapii.skipLogin) return fetch(url, opts);

  const access = await getAccessToken();
  if (!access) {
    redirectLogin();
    return new Response(null, { status: 401 });
  }

  const headers = new Headers(opts.headers || {});
  headers.set("Authorization", "Bearer " + access);
  let resp = await fetch(url, { ...opts, headers });

  if (resp.status === 401) {
    const refreshed = await doRefresh();
    if (!refreshed) {
      redirectLogin();
      return resp;
    }
    const retryHeaders = new Headers(opts.headers || {});
    retryHeaders.set("Authorization", "Bearer " + localStorage.getItem(ACCESS_KEY));
    resp = await fetch(url, { ...opts, headers: retryHeaders });
    if (resp.status === 401) redirectLogin();
  }
  return resp;
};

export async function establecerSesion() {
  if (AppConfig.lcapii.skipLogin) {
    localStorage.setItem("nombres", "Dev User");
    localStorage.setItem("urlFoto", "");
    localStorage.setItem("sucursales", JSON.stringify([0]));
    return true;
  }
  if (!localStorage.getItem(ACCESS_KEY)) {
    redirectLogin();
    return false;
  }
  const r = await apiFetch(LCAPII_URL + "/usuario/validar?sistema=" + LCAPII_SID);
  if (!r.ok) {
    redirectLogin();
    return false;
  }
  const data = await r.json();
  localStorage.setItem("nombres", data.nombres || data.Nombres || "");
  localStorage.setItem("urlFoto", data.urlFoto || data.UrlFoto || "");
  try {
    const datosUsu = JSON.parse(data.datosUsuario || data.DatosUsuario || "{}");
    localStorage.setItem("sucursales", JSON.stringify(datosUsu.sucursales || [0]));
  } catch {
    localStorage.setItem("sucursales", JSON.stringify([0]));
  }
  return true;
}

/** Permisos asignados al usuario en este sistema (ids de LCApii). */
export async function permisosAsignados() {
  if (AppConfig.lcapii.skipLogin) return Object.values(AppConfig.lcapii.permisos);
  try {
    const r = await apiFetch(LCAPII_URL + "/usuario/PermisosDeSistema/" + LCAPII_SID);
    const resp = await r.json();
    return resp.filter((p) => p.asignado).map((p) => p.id);
  } catch (e) {
    console.error("Error permisos:", e);
    return [];
  }
}

export async function logout() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  try {
    await fetch(LCAPII_URL + "/usuario/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ RefreshToken: refreshToken || "" }),
    });
  } catch {
    /* idempotente */
  }
  localStorage.clear();
  window.location.href = LCAPII_LOGIN_SHELL;
}

export const getUrlFoto = () => localStorage.getItem("urlFoto") || LCAPII_URL + "/Usuarios/0.webp";
export const getNombre = () => localStorage.getItem("nombres") || "";
export const CAMBIAR_CLAVE_URL = LCAPII_URL + "/auth/chPass.html";
